import React from "react";

interface LoyaltyScopeDeniedNoticeProps {
    show: boolean;
    onDismiss: () => void;
}

// e2e accounts-ui hub surface where a traveler manages per-airline consent
// grants, including re-granting a scope they previously denied. Matches the
// pattern of other hardcoded e2e URLs in this demo (see movmoIdentity.ts).
const CONNECTED_APPS_URL = "https://accounts.e2e.movmo.io/connected-apps";

/**
 * MOVMO-542 (denied-scope follow-up): dismissible bottom-left notice shown
 * when the loyalty-link modal's grant-half-of-late-link interstitial
 * (LoyaltyLinkModal.tsx's "One more step" screen) round-trips through Movmo
 * OAuth but comes back with loyalty.read still ungranted because the
 * traveler had explicitly DENIED that scope for Philippine Airlines
 * beforehand (hub toggle off) rather than merely never having decided.
 *
 * A scope requested by name (loyalty.read is named explicitly in
 * GRANT_LOYALTY_SCOPE) is RE-ASKED even after a prior denial — auth-ui
 * shows the delta-consent screen again with a "previously turned off"
 * label so the traveler can reconsider (only a scope a client never
 * requests by name is skipped without a prompt; see MOVMO-455's consent
 * model). This notice only appears when the traveler declines AGAIN on
 * that re-ask: without it, that outcome is still a dead end — click
 * "Turn on sharing", decline once more, land back with the header
 * unchanged and no explanation. This tells them where the real re-grant
 * surface lives (their own Movmo hub, not another OAuth attempt from
 * here).
 *
 * Positioned like LoyaltySyncToast (bottom-left; the Movmo chip lives in
 * the landing page flow) but visually distinct (amber accent, not
 * the toast's brand blue) since this is guidance/friction, not a success
 * confirmation. Manually dismissed only — no auto-dismiss timer — since it
 * carries an action link the traveler may need a moment to read and use.
 */
const LoyaltyScopeDeniedNotice: React.FC<LoyaltyScopeDeniedNoticeProps> = ({
    show,
    onDismiss,
}) => {
    if (!show) return null;

    return (
        <div
            className="fixed bottom-6 left-6 z-40 w-full rounded-lg border-l-4 border-warning-700 bg-white shadow-lg"
            style={{ maxWidth: 360 }}
            role="status"
        >
            <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1">
                    <p className="text-sm font-semibold text-grayWarm-950">
                        Loyalty sharing stayed off
                    </p>
                    <p className="mt-0.5 text-xs text-grayWarm-600">
                        You chose not to share your loyalty status with
                        Philippine Airlines. You can turn it on anytime
                        under Connected apps in your Movmo account.
                    </p>
                    <a
                        href={CONNECTED_APPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-block text-xs font-semibold text-grayWarm-700 underline transition-colors hover:text-grayWarm-950"
                    >
                        Open Connected apps ↗
                    </a>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-grayWarm-400 transition-colors hover:text-grayWarm-600"
                    aria-label="Dismiss"
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export default LoyaltyScopeDeniedNotice;
